import { createSelector } from 'reselect'
import { get, values, map, isEmpty, find, head, filter, includes, replace } from 'lodash'
import { getDevice } from 'store/Devices/selector'
import { getPrinter, getPrinters } from 'store/Printers/selector'
import { getRevenues } from 'store/Revenues/selector'
import { getMenus } from 'store/Menus/selector'
import { formattedConfigurationLabel } from 'utils/devices'

export const getConfigurations = (state) => values(get(state, ['configurations', 'byId'], []))

export const getConfigurationByDeviceId = (state, deviceId) =>
  find(getConfigurations(state), ['deviceId', deviceId])

export const getPrintersWithAssociatedConfiguration = createSelector(
  getConfigurations,
  getPrinters,
  (configurations, printers) => {
    const configurationWithAssociatedPrinter = printers.reduce((acc, item) => {
      const configurationWithAssociatedPrinter = configurations.find((configuration) => {
        const controlledPrinterUuids = configuration?.configuration?.controlledPrinterUuids ?? []
        return includes(controlledPrinterUuids, item.uuid)
      })

      const port = replace(item.port, 'TCP:', '')

      const newItem = {
        ...item,
        port,
        configuration: {
          name: configurationWithAssociatedPrinter?.name,
          uuid: configurationWithAssociatedPrinter?.uuid,
        },
      }

      return [...acc, newItem]
    }, [])

    return configurationWithAssociatedPrinter
  },
)

export const getPrintControllerConfigurations = createSelector(
  getConfigurations,
  (configurations) =>
    map(configurations, (configuration) => ({
      name: get(configuration, 'name'),
      uuid: get(configuration, 'uuid'),
      configuration: get(configuration, 'configuration'),
      controlledPrinters: get(configuration, 'configuration.controlledPrinterUuids') || [],
    })),
)

export const getConfigurationsWithDevicesAndPrinters = createSelector(
  getConfigurations,
  getDevice,
  getPrinters,
  (configurations, device, printers) =>
    map(configurations, (configuration) => {
      const receiptPrinterName = find(
        printers,
        (printer) =>
          printer.uuid === get(configuration, 'configuration.receiptPrinterUuid', undefined),
      )

      const controlledPrinterUuids = configuration?.configuration?.controlledPrinterUuids ?? []

      const controlledPrinters = filter(printers, (printer) => {
        return includes(controlledPrinterUuids, printer.uuid)
      })

      return {
        ...configuration,
        deviceName: get(device, 'name'),
        printerName: get(receiptPrinterName, 'name'),
        controlledPrinters,
      }
    }),
)

export const getConfiguration = (state, uuid) => get(state, ['configurations', 'byId', uuid], {})

export const getConfigurationWithResources = createSelector(
  getConfiguration,
  getDevice,
  getPrinter,
  getRevenues,
  getMenus,
  (configuration, device, printer, revenues, menus) => {
    const menuUuids = get(configuration, 'configuration.menuUuids', '')

    const revenueCenterWithSelectedMenus = find(revenues, (revenueCenter) => {
      return revenueCenter.menus.some((menu) => menu.uuid === head(menuUuids))
    })

    const selectedMenus = map(
      filter(menus, (menu) => menuUuids.includes(menu.uuid)),
      (selectedMenu) => ({
        label: selectedMenu.name || 'Unamed menu',
        value: selectedMenu.uuid,
      }),
    )

    const selectedXpediteRevenueCenter = configuration?.configuration?.standUuids?.map(
      (standUuid) => ({
        value: standUuid,
        label: revenues?.find((revenue) => revenue.uuid === standUuid)?.name ?? '',
      }),
    ) ?? [{ label: '', value: '' }]

    return {
      ...configuration,
      deviceName: get(device, 'name', undefined),
      receiptPrinterUuid: get(printer, 'receiptPrinterUuid', ''),
      selectedMenus,
      selectedXpediteRevenueCenter,
      selectedSingleRevenue: [
        {
          label: get(revenueCenterWithSelectedMenus, 'name', ''),
          value: get(revenueCenterWithSelectedMenus, 'uuid', ''),
        },
      ],
    }
  },
)

export const getSelectableConfigurations = createSelector(getConfigurations, (configurations) =>
  configurations
    .filter((configuration) => isEmpty(configuration.deviceId) && !isEmpty(configuration.name))
    .map((configuration) => ({
      label: formattedConfigurationLabel(configuration),
      value: configuration.uuid,
    })),
)

export const getConfigurationSearchQuery = (state) =>
  state?.configurations?.filters?.searchQuery ?? ''
